import React, { useEffect, useState } from 'react';
import { AssessmentCardSkeleton } from 'src/common';
import useSelectedAssessment from '../useSelectedAssessment';
import { useDashboardQuery } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql';
import { TileContainer } from 'src/components/Tile';
import {
  ExpectedLoss,
  CurrentSecurityProgram,
  FutureSecurityProgram,
  ReturnInvestment,
  RiskyScenariosV2,
  ControlImplementationV2,
  OverdueReviews,
  AIFV2,
  RiskAvoidanceV2,
  UpcomingActions,
  Overview,
  MissingData,
} from './widgets';
import { AssessmentState } from './types';
import { determineState } from './util';
import { AddIncidents, Banner, Head } from './comps';
import { FiltersType } from 'src/api/types/dashboard';
import { useQuery } from 'react-query';
import { QKeys } from 'src/api/types';
import { getTimelineActions } from 'src/api/dashboards';

//All information available
//No scenarios
//Scenarios, but no linked controls
//Scenarios, linked controls, but no modifiers
//Scenarios, linked only live controls, modifiers
//Scenarios, linked only not started/in progress controls, modifiers

const V2 = () => {
  const [assessmentState, setAssessmentState] = useState<AssessmentState>('ALL_INFO_AVAILABLE');
  const { setSelectedAssessmentStored, selectedAssessment } = useSelectedAssessment();

  const { data, loading, networkStatus } = useDashboardQuery({
    variables: { assessmentId: selectedAssessment },
    skip: !selectedAssessment,
  });
  const isLoading = isQueryLoading({ loading, data, networkStatus });

  const filters: FiltersType = {
    objects: {
      assessments: {
        [data?.assessment?.name!]: {
          tag: data?.assessment?.id!,
          enabled: true,
        },
      },
      scenarios: {},
      controls: {},
      incidents: {},
    },
    events: {
      Assessments: {},
      Controls: {},
      Scenarios: {},
      Incidents: {},
      Actions: {},
    },
    owners: {},
  };

  const { data: timeline, isLoading: isTimelineLoading } = useQuery([QKeys.DashboardTimelineActions, { filters }], () =>
    getTimelineActions({ filters }),
  );

  useEffect(() => {
    if (data?.assessment) {
      setAssessmentState(determineState(data.assessment));
    }
  }, [data]);

  const isEmpty = !isLoading && !selectedAssessment;

  const hasIncidents = data?.assessment?.scenarios?.some((scenario) => !!scenario.incidents.length);

  return (
    <>
      <Head
        isEmpty={isEmpty}
        selectedAssessment={selectedAssessment}
        setSelectedAssessmentStored={setSelectedAssessmentStored}
        data={data}
      />

      <Banner state={assessmentState} assessmentId={selectedAssessment} />

      {!isLoading && !isTimelineLoading ? (
        <TileContainer>
          <Overview state={assessmentState} value={data?.assessment} />
          <ExpectedLoss state={assessmentState} value={data?.dashboard.expectedLoss} />
          <RiskyScenariosV2 state={assessmentState} value={data?.dashboard.riskyScenarios} />
          <CurrentSecurityProgram state={assessmentState} value={data?.dashboard.securityProgram} />
          <FutureSecurityProgram state={assessmentState} value={data?.dashboard.futureSecurityProgram} />
          <ReturnInvestment state={assessmentState} value={data?.dashboard.returnOnInvestment} />
          <ControlImplementationV2 state={assessmentState} value={data?.dashboard.controlImplementation} />
          <OverdueReviews state={assessmentState} value={data?.dashboard.overdueReviews} />
          <UpcomingActions activityData={timeline?.data || []} />
          <AIFV2
            state={assessmentState}
            value={data?.dashboard.averageIncidentFrequency}
            noIncidentsComponent={!hasIncidents ? <AddIncidents /> : undefined}
          />
          <RiskAvoidanceV2
            state={assessmentState}
            value={data?.dashboard.riskAvoidance}
            noIncidentsComponent={!hasIncidents ? <AddIncidents /> : undefined}
          />
          <MissingData state={assessmentState} value={data?.dashboard.missingData} />
        </TileContainer>
      ) : (
        <TileContainer>
          {Array.from({ length: 6 }).map((_, idx) => (
            <AssessmentCardSkeleton key={idx} />
          ))}
        </TileContainer>
      )}
    </>
  );
};

export default V2;
